import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sunrise } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { usePortfolioReports, fmtAud, fmtPct } from "@/hooks/usePortfolioReport";

/** Signed-in users with a saved Swyftx key only. Renders nothing otherwise. */
export function MorningReportCard() {
  const { user } = useAuth();
  const { data: reports } = usePortfolioReports(1);
  const latest = reports?.[0];
  const prevStatus = useRef<string | undefined>();

  // In-app notification for users who have the site open when the report finishes.
  useEffect(() => {
    if (prevStatus.current === "running" && latest?.status === "ready") {
      toast.success("Your portfolio report is ready", { action: { label: "Read", onClick: () => (window.location.href = "/my-report") } });
    }
    prevStatus.current = latest?.status;
  }, [latest?.status]);

  if (!user || !latest) return null;

  if (latest.status === "running") {
    return (
      <Card className="border-primary/30"><CardContent className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Building your portfolio report from live Swyftx data…
      </CardContent></Card>
    );
  }
  if (latest.status !== "ready" || !latest.summary) return null;
  const s = latest.summary;

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-card to-primary/5">
      <CardContent className="p-4 md:p-5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Sunrise className="w-4 h-4 text-primary" />
            <span className="type-eyebrow">Your morning report · {new Date(latest.report_date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
          </div>
          <div className="flex gap-2">
          <Button asChild size="sm" variant="ghost"><Link to="/portfolio">Portfolio</Link></Button>
          <Button asChild size="sm" variant="outline" className="gap-1">
            <Link to="/my-report">Read full report <ArrowRight className="w-3 h-3" /></Link>
          </Button>
          </div>
        </div>
        {s.market_vibe && <p className="text-sm text-muted-foreground">{s.market_vibe}</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Portfolio value" value={fmtAud(s.total_aud)} sub={`${s.holdings_count} holdings`} />
          <Stat label="24h change" value={fmtPct(s.change_24h_pct)} sub={s.change_24h_aud != null ? fmtAud(s.change_24h_aud) : undefined} tone={s.change_24h_pct} />
          <Stat label="Top winners 24h" value={s.top_winners.map((w) => w.symbol).join(", ") || "n/a"} sub={s.top_winners.map((w) => fmtPct(w.pct)).join(" · ")} />
          <Stat label="Top losers 24h" value={s.top_losers.map((w) => w.symbol).join(", ") || "n/a"} sub={s.top_losers.map((w) => fmtPct(w.pct)).join(" · ")} />
        </div>
        <div className="text-xs text-muted-foreground">
          Concentration risk: <span className="text-foreground font-medium">{s.concentration_risk ?? "n/a"}</span>
          {s.largest_position && <> · largest position {s.largest_position.symbol} ({s.largest_position.share_pct?.toFixed(0)}%)</>}
        </div>
        {s.top_actions.length > 0 && (
          <ul className="text-sm space-y-1 list-disc pl-5">
            {s.top_actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        )}
        <p className="text-[11px] text-muted-foreground">Not financial advice. Prices from live Swyftx data at report time.</p>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: number | null }) {
  const cls = tone == null ? "text-foreground" : tone >= 0 ? "text-success" : "text-destructive";
  return (
    <div className="rounded-md bg-card/70 border border-border/50 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`type-data-sm font-bold truncate ${cls}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
    </div>
  );
}
